import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UsersThree, UserCircle } from "@phosphor-icons/react";

/**
 * ReassignCandidateModal — admin/HM tool for handing a candidate's
 * ownership to a different recruiter (or into the shared team pool).
 *
 * Endpoint: POST /api/candidates/{id}/reassign
 * Body: { new_owner_id?, to_team_pool?, reason? }
 *
 * Design goals:
 * - Zero-training UI: pick "who owns this now" from a list, hit Save.
 * - Team pool option (radio) is always visible so admins can quickly
 *   'liberate' a stuck candidate.
 * - Free-text reason (optional but strongly encouraged for audit trail).
 */
export default function ReassignCandidateModal({
    candidateId,
    candidateName,
    currentOwnerId,
    currentOwnerName,
    isTeamPool,
    onClose,
    onSaved,
}) {
    const [teammates, setTeammates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(isTeamPool ? "recruiter" : "recruiter"); // "recruiter" | "pool"
    const [selectedId, setSelectedId] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/users");
                const recs = (data || []).filter((u) =>
                    ["recruiter", "admin", "hiring_manager"].includes(u.role) &&
                    u.id !== currentOwnerId,
                );
                setTeammates(recs);
                if (recs.length && !selectedId) setSelectedId(recs[0].id);
            } catch (_) {
                toast.error("Couldn't load your team list.");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOwnerId]);

    const save = async () => {
        if (mode === "recruiter" && !selectedId) {
            toast.error("Pick a recruiter to reassign to.");
            return;
        }
        setSaving(true);
        try {
            const body =
                mode === "pool"
                    ? { to_team_pool: true, reason: reason.trim() || undefined }
                    : { new_owner_id: selectedId, reason: reason.trim() || undefined };
            const { data } = await api.post(`/candidates/${candidateId}/reassign`, body);
            toast.success(data?.message || "Candidate reassigned.");
            onSaved?.();
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Could not reassign the candidate.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="reassign-modal"
            className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="bg-white w-full max-w-lg shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-[#E5E5E5] px-5 py-3.5">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                            Reassign candidate
                        </p>
                        <h2 className="text-lg font-bold tracking-tight text-ink truncate">
                            {candidateName || "Candidate"}
                        </h2>
                        <p className="text-xs text-[#525252]">
                            Currently owned by <span className="font-bold">{currentOwnerName || (isTeamPool ? "Team pool" : "—")}</span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="reassign-modal-close"
                        className="text-[#525252] hover:text-ink"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    {/* Mode selector */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                            Reassign to
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                data-testid="reassign-mode-recruiter"
                                onClick={() => setMode("recruiter")}
                                className={`px-3 py-2.5 border text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
                                    mode === "recruiter"
                                        ? "bg-cobalt text-white border-cobalt"
                                        : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                }`}
                            >
                                <UserCircle size={14} weight="bold" /> A teammate
                            </button>
                            <button
                                type="button"
                                data-testid="reassign-mode-pool"
                                onClick={() => setMode("pool")}
                                className={`px-3 py-2.5 border text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
                                    mode === "pool"
                                        ? "bg-cobalt text-white border-cobalt"
                                        : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                }`}
                            >
                                <UsersThree size={14} weight="bold" /> Team pool
                            </button>
                        </div>
                        {mode === "pool" && (
                            <p className="text-[11px] text-[#525252] leading-snug">
                                Moves this candidate to the shared pool — every recruiter
                                in the workspace can view and claim them.
                            </p>
                        )}
                    </div>

                    {/* Teammate picker */}
                    {mode === "recruiter" && (
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                                New owner
                            </label>
                            {loading ? (
                                <p className="text-xs text-[#A3A3A3]">Loading team…</p>
                            ) : teammates.length === 0 ? (
                                <p className="text-xs text-[#B45309]">
                                    No other teammates to reassign to.
                                </p>
                            ) : (
                                <select
                                    data-testid="reassign-recruiter-select"
                                    value={selectedId}
                                    onChange={(e) => setSelectedId(e.target.value)}
                                    className="w-full bg-white border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                >
                                    {teammates.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name || t.email} {t.role !== "recruiter" ? `(${t.role})` : ""}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* Reason */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                            Reason (optional, kept for audit trail)
                        </label>
                        <textarea
                            data-testid="reassign-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Ravi has left the team, moving to Priya"
                            rows={3}
                            className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt resize-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-[#E5E5E5] px-5 py-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="reassign-cancel-btn"
                        className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-ink transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || (mode === "recruiter" && !selectedId)}
                        data-testid="reassign-save-btn"
                        className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                        {saving ? "Reassigning…" : "Reassign"}
                    </button>
                </div>
            </div>
        </div>
    );
}
